// @ts-nocheck
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { enrollActions } from 'ducks/courses/actions';
import { sectionActions } from 'ducks/section/actions';
import { isEnabledPremiumEnroll } from 'settings/';
import { useStepper } from 'utils/customHooks';
import { currencyFormatter } from 'utils/currencyHelpers';
import { ReactComponent as SuccessIcon } from 'assets/svg/success.svg';
import ChoosePlan from 'screens/EnrollCourse/ChoosePlan';
import FreePlan from 'screens/EnrollCourse/FreePlan';
import NooderaModal from 'components/NooderaModal';
import Stepper from 'components/Stepper';
import Payment from '../Payment/index';
import Section from '../Section/index';
import { PaidCourseIcon, Header, StepperContainer } from './EnrollCourseModal.styles';
import type { EnrollCourseModalProps } from './EnrollCourseModal.types';

const paidSteps = [
  {
    label: 'Payment',
    key: 'payment',
  },
  {
    label: 'Team',
    key: 'section',
  },
];
const freeSteps = [
  {
    label: 'Join a Team',
  },
];
const paidPlanDetails = [
  'Certificate on completion',
  'Schedule and launch video sessions with other learners',
  'Collaborate with annotations on course content',
  'Access to instructional support and assignment grading (for additional fees)',
];
const freePlanDetails = [
  'Chat with other learners one-one-one or in groups through private channels',
  'Schedule and launch synchronous sessions up to 40 minutes with other learners',
  'Leverage chatbots to answer common course questions',
];
const planLabels = {
  premium: 'Premium',
  audit: 'Audit',
  courseTitle: 'Applied Data Science with SQL',
  paidSectionTitle: 'Choose the best plan for your goals',
  freeSectionTitle: 'Choose the best plan for your career',
};

const EnrollCourseModal = ({
  isOpen,
  onClose,
  courseData,
}: EnrollCourseModalProps): React.ReactElement<any> => {
  const dispatch = useDispatch();
  const sections = useSelector((state) => state.section.sections.data);
  const isLoadingSections = useSelector((state) => state.section.sections.isLoading);
  const [headerTitle, setHeaderTitle] = React.useState({
    type: 'string',
    value: 'Join a Team',
  });
  const history = useHistory();
  const selectedSectionId = useSelector((state) => state.section?.selectedSection?.id);
  const school = courseData?.school?.university;
  const { slug, shortTitle, mode = [], priceInCents } = courseData ?? {};
  const modesLowercase = mode.map((v) => v.toLowerCase());
  const auditOnly = mode.length === 1 && mode[0].toLowerCase() === 'audit';
  const premiumOnly = mode.length === 1 && mode[0].toLowerCase() === 'premium';
  const allModes =
    Boolean(mode.length) && modesLowercase.includes('premium') && modesLowercase.includes('audit');
  const premiumPrice = priceInCents / 100;
  const [isPaidFlow, setPaidFlow] = React.useState(
    isEnabledPremiumEnroll && (premiumOnly || allModes),
  );
  const [steps, setSteps] = React.useState(auditOnly ? freeSteps : paidSteps);
  const { activeStep, handleNext } = useStepper(steps.length);
  const [isFlowInitiated, setIsFlowInitiated] = React.useState(!isEnabledPremiumEnroll);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = React.useState(false);
  React.useEffect(() => {
    setSteps(isPaidFlow && isEnabledPremiumEnroll ? paidSteps : freeSteps);
  }, [isPaidFlow]);

  const toggleFlow = (): void => setIsFlowInitiated(!isFlowInitiated);

  const showFreePlan = (): React.ReactElement<any> | null =>
    !isPaidFlow && !isFlowInitiated && (auditOnly || allModes) ? (
      <FreePlan
        cardTitle={planLabels.audit}
        courseTitle={shortTitle}
        planDetails={freePlanDetails}
        sectionTitle={allModes ? planLabels.freeSectionTitle : ''}
        onEnroll={toggleFlow}
        onGetCertificate={() => setPaidFlow(true)}
        price={0}
        university={school}
        invertColor
        showPlanSwitcher={allModes}
      />
    ) : null;

  const showPaidPlan = (): React.ReactElement<any> | null =>
    isPaidFlow && !isFlowInitiated && (premiumOnly || allModes) ? (
      <ChoosePlan
        type='paid'
        cardTitle={planLabels.premium}
        courseTitle={shortTitle}
        sectionTitle={allModes ? planLabels.paidSectionTitle : ''}
        planDetails={paidPlanDetails}
        onEnroll={toggleFlow}
        onEnrollForFree={() => setPaidFlow(false)}
        price={premiumPrice}
        university={school}
        icon={<PaidCourseIcon />}
        showPlanSwitcher={allModes}
      />
    ) : null;

  const showHeader = (): React.ReactElement<any> | null =>
    isFlowInitiated ? (
      <Header>
        {steps.length > 1 ? (
          <StepperContainer>
            <Stepper activeStep={activeStep} steps={steps} />
          </StepperContainer>
        ) : (
          headerTitle.value
        )}
      </Header>
    ) : null;

  const handleHeaderTitle = (title: string): void => {
    if (title === 'create') {
      setHeaderTitle({
        type: 'text',
        value: 'Create a Team',
      });
    } else {
      setHeaderTitle({
        type: 'text',
        value: 'Join a Team',
      });
    }
  };

  const handleFinish = (): void => {
    setIsSuccessModalOpen(true);
  };

  const handleModalClose = (): void => {
    setIsSuccessModalOpen(false);
    history.push('/');
    dispatch(sectionActions.getSectionPreviewRequest(selectedSectionId));
    dispatch(enrollActions.enrollModalReset());
  };

  const renderFreeFlowScreen = (): React.ReactElement<any> | null => {
    if (!isFlowInitiated || (isFlowInitiated && isPaidFlow)) return null;

    if (activeStep === 1) {
      return (
        <Section
          headerTitle={headerTitle}
          isLoading={isLoadingSections}
          hasSections={sections?.length > 0}
          courseSlug={slug}
          unitId={courseData?.brightspaceOrgUnitId}
          moodleCourseId={courseData?.moodleCourseId}
          lms={courseData?.lms}
          onChangeHeaderTitle={handleHeaderTitle}
          onFinish={handleFinish}
        />
      );
    }

    return null;
  };

  const getSuccessModalDescription = (): string =>
    `You're ready to embark on your learning journey! Access the course content by selecting it from your dashboard.`;

  const renderPaidFlowScreen = (): React.ReactElement<any> | null => {
    if (!isFlowInitiated || (isFlowInitiated && !isPaidFlow)) return null;

    switch (activeStep) {
      case 1:
        return (
          <Payment
            courseSlug={slug}
            onNextStep={handleNext}
            ctaText={`Pay ${currencyFormatter(
              premiumPrice,
              courseData?.parentCourse?.partner?.currency,
            )}`}
          />
        );
      case 2:
        return (
          <Section
            headerTitle={headerTitle}
            isLoading={isLoadingSections}
            hasSections={sections?.length > 0}
            courseSlug={slug}
            unitId={courseData?.brightspaceOrgUnitId}
            moodleCourseId={courseData?.moodleCourseId}
            lms={courseData?.lms}
            onChangeHeaderTitle={handleHeaderTitle}
            onFinish={handleFinish}
          />
        );

      default:
        return null;
    }
  };

  return (
    <NooderaModal title='Enroll in Course' isOpen={isOpen} onClose={onClose}>
      {showHeader()}
      {showFreePlan()}
      {showPaidPlan()}
      {renderPaidFlowScreen()}
      {renderFreeFlowScreen()}
      <NooderaModal.Small
        isOpen={isSuccessModalOpen}
        icon={<SuccessIcon />}
        title='All set!'
        description={getSuccessModalDescription()}
        btnText='OK'
        onClose={handleModalClose}
      />
    </NooderaModal>
  );
};

export default EnrollCourseModal;
